import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, FileText, CheckCircle, DollarSign, TrendingUp, AlertCircle, Award, Briefcase, LineChart, MapPin } from 'lucide-react';
import InfoTooltip from './components/common/InfoTooltip';
import DateRangePicker from './features/analytics/components/DateRangePicker';
import MetricCard from './features/analytics/components/MetricCard';
import QuoteTrendChart from './features/analytics/components/QuoteTrendChart';
import QuoteFunnelChart from './features/analytics/components/QuoteFunnelChart';
import IndustryPieChart from './features/analytics/components/IndustryPieChart';
import SalesLeaderboard from './features/analytics/components/SalesLeaderboard';
import RegionBarChart from './features/analytics/components/RegionBarChart';
import PipelineAgingSection from './features/analytics/components/PipelineAgingSection';
import ForecastSection from './features/analytics/components/ForecastSection';
import useAnalyticsData from './features/analytics/hooks/useAnalyticsData';
import { regionOptions } from './config/quoteDefaults';

/**
 * Toggle component for switching between Pipeline and Won views
 */
const ViewToggle = ({ view, onChange }) => (
  <div className="inline-flex items-center gap-2">
    <div className="inline-flex items-center bg-slate-100 rounded-lg p-1">
      <button
        onClick={() => onChange('pipeline')}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          view === 'pipeline'
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-slate-600 hover:text-slate-800'
        }`}
      >
        Pipeline
      </button>
      <button
        onClick={() => onChange('won')}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          view === 'won'
            ? 'bg-white text-green-600 shadow-sm'
            : 'text-slate-600 hover:text-slate-800'
        }`}
      >
        Won
      </button>
    </div>
    <InfoTooltip
      text="Pipeline includes Approved + Sent + Won quotes (active revenue potential). Won shows only closed deals marked as Accepted."
      position="right"
    />
  </div>
);

/**
 * Tab navigation for Dashboard vs Forecast views
 */
const AnalyticsTabs = ({ activeTab, onChange }) => (
  <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1">
    <button
      onClick={() => onChange('dashboard')}
      className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-all ${
        activeTab === 'dashboard'
          ? 'bg-white text-blue-600 shadow-sm'
          : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      <BarChart3 size={18} />
      Dashboard
    </button>
    <button
      onClick={() => onChange('forecast')}
      className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-all ${
        activeTab === 'forecast'
          ? 'bg-white text-purple-600 shadow-sm'
          : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      <LineChart size={18} />
      Forecast
    </button>
  </div>
);

/**
 * Analytics Dashboard View
 * Shows quote volume trends, revenue analytics, and sales performance metrics
 * Accessible to managers and admins only
 *
 * @param {Function} onViewQuote - Callback to view a quote by ID
 */
const AnalyticsView = ({ onViewQuote }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [viewMode, setViewMode] = useState('pipeline'); // 'pipeline' or 'won'
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' or 'forecast'
  const [selectedRegion, setSelectedRegion] = useState(''); // '' means all regions

  // Default to last 12 months
  const defaultEnd = new Date();
  const defaultStart = new Date(defaultEnd.getTime() - 365 * 24 * 60 * 60 * 1000);

  const [startDate, setStartDate] = useState(defaultStart.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(defaultEnd.toISOString().split('T')[0]);

  const { data, loading, error } = useAnalyticsData(startDate, endDate, selectedRegion || null);

  // Transform data based on view mode
  const transformedData = useMemo(() => {
    if (!data) return null;

    const isPipeline = viewMode === 'pipeline';

    return {
      ...data,
      // Transform industry data
      revenueByIndustry: data.revenueByIndustry?.map(row => ({
        industry: row.industry,
        arr: isPipeline ? row.pipelineARR : row.wonARR,
        servicesOTR: isPipeline ? row.pipelineOTR : row.wonOTR,
        count: isPipeline ? row.pipelineCount : row.wonCount
      })).filter(row => row.arr > 0 || row.servicesOTR > 0),
      // Transform region data
      revenueByRegion: data.revenueByRegion?.map(row => ({
        region: row.region,
        pipelineARR: row.pipelineARR,
        pipelineCount: row.pipelineCount,
        wonARR: row.wonARR,
        wonCount: row.wonCount
      })).filter(row => row.pipelineARR > 0 || row.wonARR > 0),
      // Transform sales data
      salesPerformance: data.salesPerformance?.map(rep => ({
        ...rep,
        arr: isPipeline ? rep.pipelineARR : rep.wonARR,
        servicesOTR: isPipeline ? rep.pipelineOTR : rep.wonOTR,
        revenue: isPipeline ? rep.pipelineRevenue : rep.wonRevenue,
        accepted: rep.wonCount,
        rate: rep.winRate
      })).filter(rep => (isPipeline ? rep.pipelineRevenue : rep.wonRevenue) > 0 || rep.quotes > 0)
    };
  }, [data, viewMode]);

  // Fetch current user to check role
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const token = localStorage.getItem('authToken');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        const response = await fetch(`${API_URL}/auth/user`, {
          headers,
          credentials: 'include'
        });

        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
    fetchUser();
  }, []);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  // Check if user has access - roles with canViewAnalytics permission
  const analyticsRoles = ['super_admin', 'admin', 'ceo', 'cro', 'cfo', 'cto', 'manager', 'presales', 'implementation', 'other'];
  const userRole = currentUser?.role?.toLowerCase();
  const hasAccess = currentUser?.permissions?.canViewAnalytics || analyticsRoles.includes(userRole);

  if (!hasAccess && currentUser) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-red-600 mb-4" />
          <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700">You don't have permission to access analytics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <BarChart3 size={32} className="text-blue-600" />
            Analytics
          </h1>
          <p className="text-slate-600 mt-1">Quote performance, sales insights, and forecasts</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Region Filter */}
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-slate-400" />
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">All Regions</option>
              {regionOptions.map(region => (
                <option key={region.value} value={region.value}>{region.label}</option>
              ))}
            </select>
          </div>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <AnalyticsTabs activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* View Mode Toggle - Only on Dashboard tab */}
      {activeTab === 'dashboard' && (
        <div className="flex items-center gap-4 mb-4">
          <ViewToggle view={viewMode} onChange={setViewMode} />
          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
            viewMode === 'pipeline'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-green-100 text-green-700'
          }`}>
            {viewMode === 'pipeline' ? (
              <>Approved + Sent + Won</>
            ) : (
              <>Won Deals Only</>
            )}
          </span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-600">Loading analytics data...</p>
        </div>
      )}

      {/* Dashboard Content */}
      {!loading && data && activeTab === 'dashboard' && (
        <>
          {/* KPI Cards - Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <MetricCard
              title="Total Quotes"
              value={data.summary.totalQuotes}
              subtitle={`${data.summary.pipelineQuotes || 0} in pipeline`}
              icon={FileText}
              color="blue"
            />
            <MetricCard
              title="Won Deals"
              value={data.summary.acceptedQuotes}
              subtitle={`${data.summary.lostQuotes || 0} lost`}
              icon={CheckCircle}
              color="green"
            />
            <div className="relative">
              <MetricCard
                title="Win Rate"
                value={`${data.summary.winRate || 0}%`}
                subtitle="Won / (Won + Lost)"
                icon={Award}
                color="purple"
              />
              <div className="absolute top-3 right-3">
                <InfoTooltip
                  text="Calculated as Won deals / (Won + Lost). Pending and draft quotes are excluded from this calculation."
                  position="left"
                  size={12}
                />
              </div>
            </div>
            <MetricCard
              title="Pending"
              value={data.summary.pendingQuotes || 0}
              subtitle="Awaiting approval"
              icon={TrendingUp}
              color="orange"
            />
          </div>

          {/* KPI Cards - Row 2: Revenue (changes based on toggle) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <MetricCard
              title={viewMode === 'pipeline' ? 'Pipeline ARR' : 'Won ARR'}
              value={formatCurrency(viewMode === 'pipeline' ? data.summary.totalARR : data.summary.wonARR)}
              subtitle={`Avg: ${formatCurrency(data.summary.avgDealSize)}`}
              icon={DollarSign}
              color={viewMode === 'pipeline' ? 'blue' : 'green'}
            />
            <MetricCard
              title={viewMode === 'pipeline' ? 'Pipeline Services' : 'Won Services'}
              value={formatCurrency(viewMode === 'pipeline' ? data.summary.totalServicesOTR : data.summary.wonServicesOTR)}
              subtitle="One-time revenue"
              icon={Briefcase}
              color="purple"
            />
            <MetricCard
              title={viewMode === 'pipeline' ? 'Total Pipeline' : 'Total Won'}
              value={formatCurrency(
                viewMode === 'pipeline'
                  ? (data.summary.totalARR || 0) + (data.summary.totalServicesOTR || 0)
                  : (data.summary.wonARR || 0) + (data.summary.wonServicesOTR || 0)
              )}
              subtitle="ARR + Services"
              icon={DollarSign}
              color={viewMode === 'pipeline' ? 'blue' : 'green'}
            />
          </div>

          {/* Funnel Chart - Full Width */}
          <div className="mb-6">
            <QuoteFunnelChart data={data.summary} />
          </div>

          {/* Pipeline Aging Section - Only shown in Pipeline view (aging doesn't apply to closed deals) */}
          {viewMode === 'pipeline' && (
            <div className="mb-6">
              <PipelineAgingSection data={data.pipelineAging} onViewQuote={onViewQuote} />
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <QuoteTrendChart data={data.volumeTrend} />
            <IndustryPieChart data={transformedData?.revenueByIndustry} viewMode={viewMode} />
          </div>

          {/* Region Distribution */}
          <div className="mb-6">
            <RegionBarChart data={transformedData?.revenueByRegion} viewMode={viewMode} />
          </div>

          {/* Sales Leaderboard */}
          <SalesLeaderboard data={transformedData?.salesPerformance} viewMode={viewMode} />
        </>
      )}

      {/* Forecast Content */}
      {!loading && data && activeTab === 'forecast' && (
        <ForecastSection
          data={data.volumeTrend}
          summary={data.summary}
          industryData={data.revenueByIndustry}
          salesData={data.salesPerformance}
        />
      )}

      {/* Empty State */}
      {!loading && !error && data && data.summary.totalQuotes === 0 && (
        <div className="bg-slate-50 rounded-xl p-12 text-center">
          <FileText size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Quote Data</h3>
          <p className="text-slate-600">No quotes found for the selected date range.</p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsView;
